import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {FileText, ChevronDown, Menu, X, LogOut, BarChart3} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import AuthModal from './AuthModal'

const Navbar = () => {
  const [isIndustriesOpen, setIsIndustriesOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [user, setUser] = useState(lumi.auth.user)
  const location = useLocation()

  useEffect(() => {
    const unsubscribe = lumi.auth.onAuthChange(({ user }) => {
      setUser(user)
    })
    return unsubscribe
  }, [])

  const handleSignOut = () => {
    lumi.auth.signOut()
  }

  const industries = [
    { name: 'Plumbing Invoice Template', slug: 'plumbing' },
    { name: 'Catering Invoice Example', slug: 'catering' },
    { name: 'Handyman Invoice', slug: 'handyman' },
    { name: 'Auto Repair Invoice', slug: 'auto-repair' },
    { name: 'Photography Invoice', slug: 'photography' },
  ]

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 text-[#0B5394] font-bold text-xl">
            <FileText className="w-6 h-6" />
            <span>Invoice Me Now</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to="/" 
              className={`text-gray-700 hover:text-[#0B5394] transition-colors ${location.pathname === '/' ? 'text-[#0B5394] font-semibold' : ''}`}
            >
              Home
            </Link>
            <Link 
              to="/templates" 
              className={`text-gray-700 hover:text-[#0B5394] transition-colors ${location.pathname === '/templates' ? 'text-[#0B5394] font-semibold' : ''}`}
            >
              Templates
            </Link>
            
            {/* Industries Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsIndustriesOpen(true)}
              onMouseLeave={() => setIsIndustriesOpen(false)}
            >
              <button className="flex items-center gap-1 text-gray-700 hover:text-[#0B5394] transition-colors">
                Industries
                <ChevronDown className="w-4 h-4" />
              </button>
              
              {isIndustriesOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
                  {industries.map((industry) => (
                    <Link
                      key={industry.slug}
                      to={`/industries/${industry.slug}`}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#0B5394] transition-colors"
                    >
                      {industry.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link 
              to="/blog" 
              className={`text-gray-700 hover:text-[#0B5394] transition-colors ${location.pathname === '/blog' ? 'text-[#0B5394] font-semibold' : ''}`}
            >
              Resources
            </Link>

            {user && (
              <>
                <Link 
                  to="/my-invoices" 
                  className={`text-gray-700 hover:text-[#0B5394] transition-colors ${location.pathname === '/my-invoices' ? 'text-[#0B5394] font-semibold' : ''}`}
                >
                  My Invoices
                </Link>
                <Link 
                  to="/analytics" 
                  className={`flex items-center gap-1 text-gray-700 hover:text-[#0B5394] transition-colors ${location.pathname === '/analytics' ? 'text-[#0B5394] font-semibold' : ''}`}
                >
                  <BarChart3 className="w-4 h-4" />
                  Analytics
                </Link>
              </>
            )}

            {user ? (
              <div className="flex items-center gap-4">
                <span className="text-gray-700 font-medium">{user.userName}</span>
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-2 text-gray-700 hover:text-[#0B5394] transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                className="bg-[#0B5394] hover:bg-[#084273] text-white px-6 py-2 rounded-lg font-semibold transition-colors"
              >
                Sign In
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-gray-700"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <Link to="/" className="block py-2 text-gray-700 hover:text-[#0B5394]">Home</Link>
            <Link to="/templates" className="block py-2 text-gray-700 hover:text-[#0B5394]">Templates</Link>
            <div className="py-2">
              <p className="text-gray-900 font-semibold mb-2">Industries</p>
              {industries.map((industry) => (
                <Link
                  key={industry.slug}
                  to={`/industries/${industry.slug}`}
                  className="block py-1 pl-4 text-sm text-gray-700 hover:text-[#0B5394]"
                >
                  {industry.name}
                </Link>
              ))}
            </div>
            <Link to="/blog" className="block py-2 text-gray-700 hover:text-[#0B5394]">Resources</Link>
            {user && (
              <>
                <Link to="/my-invoices" className="block py-2 text-gray-700 hover:text-[#0B5394]">My Invoices</Link>
                <Link to="/analytics" className="block py-2 text-gray-700 hover:text-[#0B5394]">Analytics</Link>
              </>
            )}
            {user ? (
              <button
                onClick={handleSignOut}
                className="block py-2 text-gray-700 hover:text-[#0B5394] w-full text-left"
              >
                Sign Out ({user.userName})
              </button>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                className="block py-2 text-[#0B5394] hover:text-[#084273] font-semibold w-full text-left"
              >
                Sign In
              </button>
            )}
          </div>
        )}
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => setShowAuthModal(false)}
      />
    </nav>
  )
}

export default Navbar